import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Project, ProjectPermissions } from '@models/coding/entities/project.model';
import { ProjectMember } from '@models/coding/entities/project-member.model';
import { ProjectRoleId } from '@models/coding/entities/project-role.model';
import { Developer } from '@models/coding/entities/developer.model';
import { ManualTokenStateService } from './manual-token-state.service';

const STORAGE_KEY = 'amia_project_tokens';
const PERMISSIONS_KEY = 'amia_project_permissions';

interface TokenEntry {
  token: string;
  expiresAt: string;
}

type StoryOwnerRef = { developer?: Pick<Developer, 'id'> | null } | null;

@Injectable({ providedIn: 'root' })
export class ProjectPermissionService {
  private readonly manualTokenState = inject(ManualTokenStateService);

  private readonly _currentProjectId = signal<number | null>(null);
  readonly currentProjectId: Signal<number | null> = this._currentProjectId.asReadonly();

  private readonly _permissions = signal<ProjectPermissions | null>(null);
  private _permissionsCache: Record<number, ProjectPermissions> | null = null;

  readonly canManageWorkItems: Signal<boolean | null> = computed(() => {
    if (this.manualTokenState.isActive()) return true;
    return this._permissions() === null ? null : this._permissions()!.canManageWorkItems;
  });

  readonly canManagePlanning: Signal<boolean | null> = computed(() => {
    if (this.manualTokenState.isActive()) return true;
    return this._permissions() === null ? null : this._permissions()!.canManagePlanning;
  });

  readonly canManageBugs: Signal<boolean | null> = computed(() => {
    if (this.manualTokenState.isActive()) return true;
    return this._permissions() === null ? null : this._permissions()!.canManageBugs;
  });

  readonly canManageImprovements: Signal<boolean | null> = computed(() => {
    if (this.manualTokenState.isActive()) return true;
    return this._permissions() === null ? null : this._permissions()!.canManageImprovements;
  });

  readonly canManageWorkItemsResolved = this.resolvePermission('canManageWorkItems');
  readonly canManagePlanningResolved = this.resolvePermission('canManagePlanning');
  readonly canManageBugsResolved = this.resolvePermission('canManageBugs');
  readonly canManageImprovementsResolved = this.resolvePermission('canManageImprovements');
  readonly canEditTasksResolved = this.resolvePermission('canEditTasks');
  // O backend (WorkItemTagsController) autoriza tags por papéis próprios (PO, GP, Arquiteto, Dev).
  readonly canManageTagsResolved = this.resolvePermission('canManageTags');

  private _tokenCache: Record<number, TokenEntry> | null = null;

  setCurrentProjectId(id: number | null): void {
    this._currentProjectId.set(id);
    this.restorePermissionsForProject(id);
  }

  storeProjectData(projectId: number, project: Project): void {
    this.persistPermissions(projectId, project.permissions);
    this.persistToken(projectId, project);
  }

  restorePermissionsForProject(projectId: number | null): void {
    if (projectId === null) {
      this._permissions.set(null);
      return;
    }
    const stored = this.loadPermissionsFromStorage()[projectId];
    if (!stored) {
      this._permissions.set(null);
      return;
    }
    // Garante campos obrigatórios mesmo em sessões anteriores aos campos canManagePlanning/canManageBugs.
    this._permissions.set({
      canManageWorkItems: stored.canManageWorkItems ?? false,
      canManagePlanning: stored.canManagePlanning ?? false,
      canManageBugs: stored.canManageBugs ?? false,
      canManageImprovements: stored.canManageImprovements ?? false,
      canEditTasks: stored.canEditTasks ?? false,
      canManageTags: stored.canManageTags,
    });
  }

  getProjectToken(projectId: number): string | null {
    const entry = this.loadFromStorage()[projectId];
    if (!entry) return null;
    if (new Date(entry.expiresAt) <= new Date()) {
      this.clearProjectToken(projectId);
      return null;
    }
    return entry.token;
  }

  clearProjectToken(projectId: number): void {
    const { [projectId]: _, ...rest } = this.loadFromStorage();
    this.persist(rest as Record<number, TokenEntry>);
  }

  private persistPermissions(projectId: number, permissions: ProjectPermissions | undefined): void {
    const perms = permissions ?? {
      canManageWorkItems: false,
      canManagePlanning: false,
      canManageBugs: false,
      canManageImprovements: false,
      canEditTasks: false,
    };
    const stored = this.loadPermissionsFromStorage();
    this._permissionsCache = { ...stored, [projectId]: perms };
    sessionStorage.setItem(PERMISSIONS_KEY, JSON.stringify(this._permissionsCache));

    // Atualiza o signal reativo apenas para o projeto atual, evitando
    // sobrescrita por requisições em voo de projetos anteriores.
    const currentId = this._currentProjectId();
    if (currentId === null || currentId === projectId) {
      this._permissions.set(perms);
    }
  }

  private persistToken(projectId: number, project: Project): void {
    if (!project.projectAccessToken || !project.projectAccessTokenExpiresAt) return;
    this.persist({
      ...this.loadFromStorage(),
      [projectId]: {
        token: project.projectAccessToken,
        expiresAt: project.projectAccessTokenExpiresAt,
      },
    });
  }

  private loadFromStorage(): Record<number, TokenEntry> {
    if (this._tokenCache) return this._tokenCache;
    try {
      this._tokenCache = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}');
      return this._tokenCache!;
    } catch {
      this._tokenCache = {};
      return this._tokenCache;
    }
  }

  private loadPermissionsFromStorage(): Record<number, ProjectPermissions> {
    if (this._permissionsCache) return this._permissionsCache;
    try {
      this._permissionsCache = JSON.parse(sessionStorage.getItem(PERMISSIONS_KEY) ?? '{}');
      return this._permissionsCache!;
    } catch {
      this._permissionsCache = {};
      return this._permissionsCache;
    }
  }

  private persist(data: Record<number, TokenEntry>): void {
    this._tokenCache = data;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  isProjectAdmin(members: Signal<ProjectMember[]>, userId: Signal<string | null>): Signal<boolean> {
    return computed(() => {
      if (this.manualTokenState.isActive()) return false;
      const uid = userId();
      if (!uid) return false;
      return this.getMemberRoleId(members(), uid) === ProjectRoleId.GP;
    });
  }

  /**
   * Editar o tempo real de desenvolvimento é permitido apenas para o
   * desenvolvedor responsável pelo work item (developer_id).
   *
   * Em modo de token manual, concede acesso (mesma regra de canManageWorkItems/
   * canManagePlanning), pois esse modo representa automação/agente operando
   * sobre work items — diferente de isProjectAdmin, que nega, por representar
   * uma checagem de identidade de um administrador humano específico do projeto.
   */
  canEditStoryRealTimeHours(
    userId: Signal<string | null>,
    story: Signal<StoryOwnerRef>
  ): Signal<boolean> {
    return computed(() => {
      if (this.manualTokenState.isActive()) return true;
      const uid = userId();
      const currentStory = story();
      if (!currentStory || !uid) return false;
      return currentStory.developer?.id === uid;
    });
  }

  /**
   * Configurar o horário de expediente do projeto é permitido apenas para
   * Gestor de Projeto (GP) ou Arquiteto — não há regra de propriedade de
   * recurso como em canEditStoryRealTimeHours, apenas checagem de papel.
   *
   * Em modo de token manual, concede acesso (mesma regra das demais
   * permissões elevadas), pois representa automação/agente operando sobre
   * configurações do projeto.
   */
  canManageWorkday(
    members: Signal<ProjectMember[]>,
    userId: Signal<string | null>
  ): Signal<boolean> {
    return this.hasRole(members, userId, [ProjectRoleId.GP, ProjectRoleId.Arquiteto]);
  }

  /**
   * Criar uma Concepção é permitido apenas para PO ou GP (RN01 da 2.9.1) — leitura e edição
   * manual das seções/mensagens são liberadas a qualquer membro do projeto (sem checagem aqui).
   *
   * Em modo de token manual, concede acesso (mesma regra das demais permissões elevadas),
   * pois representa automação/agente operando sobre o projeto.
   */
  /**
   * Gerenciar o Ciclo de Roadmap do projeto é permitido apenas para
   * Gestor de Projeto (GP) ou Arquiteto (RN03 da 7.6.16) — os mesmos
   * papéis de canManageWorkday, mas com semântica de negócio distinta
   * para evitar acoplamento acidental entre as duas regras.
   */
  canManageRoadmap(
    members: Signal<ProjectMember[]>,
    userId: Signal<string | null>
  ): Signal<boolean> {
    return this.hasRole(members, userId, [ProjectRoleId.GP, ProjectRoleId.Arquiteto]);
  }

  canManageUpstreamCycles(
    members: Signal<ProjectMember[]>,
    userId: Signal<string | null>
  ): Signal<boolean> {
    return this.hasRole(members, userId, [ProjectRoleId.PO, ProjectRoleId.GP]);
  }

  /**
   * Consultar o histórico de baselines para auditoria é permitido a PO, GP ou Arquiteto (RN03 da
   * 19.6.1). Semântica distinta de canManageUpstreamCycles: inclui o Arquiteto e não concede
   * nenhuma ação de gestão — o backend também responde 403.
   */
  canViewBaselineHistory(
    members: Signal<ProjectMember[]>,
    userId: Signal<string | null>
  ): Signal<boolean> {
    return this.hasRole(members, userId, [
      ProjectRoleId.PO,
      ProjectRoleId.GP,
      ProjectRoleId.Arquiteto,
    ]);
  }

  /**
   * Nome do papel do usuário atual no projeto (ex.: exibido no banner "Você está vendo como X"
   * da Concepção para papéis sem permissão de gerenciar — Critério 3 da 2.9.4). Nulo em modo de
   * token manual (automação, sem papel humano) ou se o usuário não for membro do projeto.
   */
  currentUserRoleName(
    members: Signal<ProjectMember[]>,
    userId: Signal<string | null>
  ): Signal<string | null> {
    return computed(() => {
      if (this.manualTokenState.isActive()) return null;
      const uid = userId();
      if (!uid) return null;
      return members().find((m) => m.userId === uid)?.roleName ?? null;
    });
  }

  /** Checagem genérica de papel — modo de token manual sempre concede acesso. */
  private hasRole(
    members: Signal<ProjectMember[]>,
    userId: Signal<string | null>,
    allowedRoles: number[]
  ): Signal<boolean> {
    return computed(() => {
      if (this.manualTokenState.isActive()) return true;
      const uid = userId();
      if (!uid) return false;
      const roleId = this.getMemberRoleId(members(), uid);
      return roleId !== null && allowedRoles.includes(roleId);
    });
  }

  private isElevatedRole(roleId: number | null): boolean {
    return roleId === ProjectRoleId.GP || roleId === ProjectRoleId.Arquiteto;
  }

  private getMemberRoleId(members: ProjectMember[], userId: string): number | null {
    return members.find((m) => m.userId === userId)?.roleId ?? null;
  }

  private resolvePermission(key: keyof ProjectPermissions): Signal<boolean> {
    return computed(
      () =>
        this.manualTokenState.isActive() ||
        this._permissions() === null ||
        !!this._permissions()![key]
    );
  }
}
